import {
    AGENCY_TYPES,
    isAgencyRole,
    isEmergencyAgencyRole,
    isLytRole,
    isTrafficViewerRole,
    isTransitAgencyRole,
} from 'src/constants/roles.constants';
import { TEAM_CONTEXT } from 'src/components/modals/modal-role-options';

/**
 * Resolve a jurisdiction record or ID to a display name.
 * @param {Object|string} jurisdiction
 * @param {Array<{ id: string, name: string }>} agencyJurisdictions
 * @returns {string|null}
 */
export const resolveJurisdictionName = (jurisdiction, agencyJurisdictions = []) => {
    if (typeof jurisdiction === 'string') {
        const trimmed = jurisdiction.trim();
        if (!trimmed) {
            return null;
        }

        const match = agencyJurisdictions.find((item) => item.id === trimmed);
        return match?.name || trimmed;
    }

    if (jurisdiction?.name?.trim()) {
        return jurisdiction.name.trim();
    }

    if (jurisdiction?.id) {
        const match = agencyJurisdictions.find((item) => item.id === jurisdiction.id);
        return match?.name || jurisdiction.id;
    }

    return null;
};

/**
 * Resolve jurisdiction records/IDs to unique display names.
 * @param {Array<Object|string>} jurisdictions
 * @param {Array<{ id: string, name: string }>} agencyJurisdictions
 * @returns {string[]}
 */
export const resolveJurisdictionNames = (jurisdictions = [], agencyJurisdictions = []) => {
    if (!Array.isArray(jurisdictions)) {
        return [];
    }

    return [...new Set(
        jurisdictions
            .map((jurisdiction) => resolveJurisdictionName(jurisdiction, agencyJurisdictions))
            .filter(Boolean),
    )].sort((left, right) => left.localeCompare(right));
};

/**
 * Label for the agency workspace row in User Details.
 * @param {{ role: string, agencyType?: string|null }} params
 * @returns {string}
 */
export const getAgencyFieldLabel = ({ role, agencyType = null }) => {
    if (isLytRole(role)) {
        return 'Agency';
    }

    if (isEmergencyAgencyRole(role)) {
        return 'Emergency Agency';
    }

    if (isTrafficViewerRole(role) && agencyType === AGENCY_TYPES.EMERGENCY) {
        return 'Emergency Agency';
    }

    if (isTransitAgencyRole(role) || isTrafficViewerRole(role)) {
        return 'Transit Agency';
    }

    if (agencyType === AGENCY_TYPES.EMERGENCY) {
        return 'Emergency Agency';
    }

    return 'Transit Agency';
};

/**
 * Workspace name shown in the agency row.
 * @param {{ role: string, agencyName?: string|null }} params
 * @returns {string}
 */
export const getAgencyDisplayValue = ({ role, agencyName = null }) => {
    if (isLytRole(role)) {
        return 'LYT';
    }

    return agencyName?.trim() || 'N/A';
};

/**
 * Traffic partners / jurisdictions the user can view.
 * @param {{ role: string, userJurisdictions?: Array, agencyJurisdictions?: Array }} params
 * @returns {string[]}
 */
export const getViewingRights = ({
    role,
    userJurisdictions = [],
    agencyJurisdictions = [],
}) => {
    if (isLytRole(role)) {
        return [];
    }

    if (isTrafficViewerRole(role)) {
        return resolveJurisdictionNames(userJurisdictions, agencyJurisdictions);
    }

    if (isAgencyRole(role)) {
        return resolveJurisdictionNames(agencyJurisdictions, agencyJurisdictions);
    }

    return [];
};

/**
 * Whether agency / viewing-rights rows appear in User Details.
 * @param {string|undefined} teamContext
 * @returns {boolean}
 */
export const shouldShowWorkspaceDetails = (teamContext) => teamContext !== TEAM_CONTEXT.LYT;
